// Publish a single jQuery instance on `window` before any jQuery plugin is
// imported. ES module imports are hoisted and evaluated before sibling
// statements, so this MUST live in its own module that vendor bootstraps import
// first — otherwise plugins that read `window.jQuery` at import time (ace's
// jquery-ace, select2, bootstrap) could evaluate before the assignment.
//
// webpack's ProvidePlugin additionally injects `$`/`jQuery` into any module
// that references them as free variables, which covers the classic
// `(function ($) { ... })(jQuery)` plugin wrappers.
import jquery from "jquery";

// jQuery 4 removed a raft of static helpers ($.proxy, $.trim, $.isFunction,
// $.isArray, $.type, $.camelCase, $.now, ...) that the bundled legacy plugins
// (Bootstrap 2, daterangepicker, bootstrap-datepicker, inspinia, turbolinks,
// stickem, scrollTo, …) still call. jQuery Migrate restores them, patching the
// shared jQuery instance (its UMD `require("jquery")` resolves to the same
// aliased module). Imported here — before any plugin — so the restored APIs
// exist by the time the plugins evaluate.
import "jquery-migrate";

window.$ = window.jQuery = jquery;

// jQuery 4 changed how same-domain `dataType: "script"` requests are transported.
// jQuery 3 fetched them over XHR and ran the body through `globalEval`; jQuery 4
// instead injects a `<script>` tag (see `canUseScriptTag` — any async, non-JSONP
// script request now qualifies). A `<script src>` GET sends the browser's default
// html-first `Accept` and no `X-Requested-With`, so Rails renders the HTML page
// instead of the `.js.erb` response, and the browser then refuses to execute the
// `text/html` body ("strict MIME type checking"). That silently broke every
// `$.getScript(...)`-loads-server-JS endpoint in Studio (paginator per-page
// reloads, subscriber show, the campaign mailing-list picker).
//
// A `<script>` tag cannot carry request headers, so giving the request a headers
// object forces jQuery back to the XHR transport, which restores `Accept:
// text/javascript` and the same-domain `X-Requested-With` header Rails needs to
// pick the JS format — i.e. the jQuery 3 behavior these endpoints were written for.
jquery.ajaxPrefilter("script", function (options) {
  if (!options.crossDomain && !options.headers) {
    options.headers = { "X-Requested-With": "XMLHttpRequest" };
  }
});

// jQuery 4 also stopped recognizing a JavaScript response when the request did
// not explicitly ask for `dataType: "script"`: it dropped the `contents.script`
// content-type mapping, so evaluating a response now requires opting in up
// front (see jquery/jquery gh-2432, gh-4822). jquery_ujs sends every
// `data-remote` request without a dataType and lets the response's
// `Content-Type` pick the handling, which is exactly how Rails answers these
// endpoints — `text/javascript` rendered from a `.js.erb`. With no mapping the
// body arrives as an inert string and is never run, so `data-remote` links and
// forms silently do nothing: the request succeeds, `ajax:success` fires, and
// the page never changes. Kaminari's `remote: true` page links are the visible
// case (CHO-6472), but this covers every `.js.erb` endpoint in Studio.
//
// Restoring the mapping restores the jQuery 3 semantics these endpoints were
// written against. Only a response served with a JavaScript content type is
// evaluated, and the requests that reach here are same-origin Rails endpoints.
jquery.ajaxSetup({ contents: { script: /\b(?:java|ecma)script\b/ } });

export default jquery;
